import React, { useState, useCallback } from 'react';
import { NewsItem } from '../../types/conference';

interface NewsManagementProps {
  news: NewsItem[];
  onCreateNews: (item: Omit<NewsItem, 'id'>) => Promise<void>;
  onUpdateNews: (item: NewsItem) => Promise<void>;
  onDeleteNews: (id: string) => Promise<void>;
}

// Move NewsForm outside the NewsManagement component to prevent recreation on every render
interface NewsFormProps {
  item: Partial<NewsItem>;
  onSubmit: (e: React.FormEvent) => Promise<void>;
  submitLabel: string;
  onNewsUpdate: (field: string, value: any) => void;
  onCancel: () => void;
}

const NewsForm: React.FC<NewsFormProps> = React.memo(({ 
  item, 
  onSubmit, 
  submitLabel, 
  onNewsUpdate,
  onCancel
}) => {
  const handleChange = useCallback((field: string, value: any) => {
    onNewsUpdate(field, value);
  }, [onNewsUpdate]);

  return (
    <form onSubmit={onSubmit} className="space-y-4 p-4 bg-gray-50 rounded-lg">
      <div>
        <label className="block text-sm font-medium text-gray-700">Title</label>
        <input
          type="text"
          value={item.title || ''}
          onChange={(e) => handleChange('title', e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          required
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700">
          Content
          <span className="ml-2 text-xs text-gray-500 font-normal">(Markdown supported)</span>
        </label>
        <textarea
          value={item.content || ''}
          onChange={(e) => handleChange('content', e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 font-mono text-sm"
          rows={10}
          required
          placeholder="Enter content using Markdown formatting..."
        />
        <p className="mt-1 text-xs text-gray-500">
          Use Markdown: **bold**, *italic*, [link](url), # headings, - lists, etc.
        </p>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Publish Date</label>
          <input
            type="date"
            value={item.publishDate || ''}
            onChange={(e) => handleChange('publishDate', e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Author</label>
          <input
            type="text"
            value={item.author || ''}
            onChange={(e) => handleChange('author', e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            required
          />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Category</label>
          <select
            value={item.category || 'announcement'}
            onChange={(e) => handleChange('category', e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            required
          >
            <option value="announcement">Announcement</option>
            <option value="update">Update</option>
            <option value="highlight">Highlight</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Status</label>
          <select
            value={item.status || 'published'}
            onChange={(e) => handleChange('status', e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            required
          >
            <option value="published">Published</option>
            <option value="draft">Draft</option>
          </select>
        </div>
        <div className="flex items-center mt-8">
          <input
            type="checkbox"
            checked={item.featured || false}
            onChange={(e) => handleChange('featured', e.target.checked)}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label className="ml-2 block text-sm text-gray-900">
            Featured News
          </label>
        </div>
      </div>
      <div className="flex justify-end space-x-3">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
        >
          {submitLabel}
        </button>
      </div>
    </form>
  );
});

const NewsManagement: React.FC<NewsManagementProps> = ({
  news,
  onCreateNews,
  onUpdateNews,
  onDeleteNews,
}) => {
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [editingNews, setEditingNews] = useState<NewsItem | null>(null);
  const [newItem, setNewItem] = useState<Omit<NewsItem, 'id'>>({
    title: '',
    content: '',
    publishDate: new Date().toISOString().split('T')[0],
    author: '',
    category: 'announcement',
    featured: false,
    status: 'published',
  });

  const handleSubmitNew = async (e: React.FormEvent) => {
    e.preventDefault();
    await onCreateNews(newItem);
    setIsAddingNew(false);
    setNewItem({
      title: '',
      content: '',
      publishDate: new Date().toISOString().split('T')[0],
      author: '',
      category: 'announcement',
      featured: false,
      status: 'published',
    });
  };

  const handleSubmitEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingNews) {
      await onUpdateNews(editingNews);
      setEditingNews(null);
    }
  };

  const handleUpdateNewItem = useCallback((field: string, value: any) => {
    setNewItem(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleUpdateEditingNews = useCallback((field: string, value: any) => {
    setEditingNews(prev => prev ? { ...prev, [field]: value } : null);
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">News Management</h2>
        <button
          onClick={() => setIsAddingNew(true)}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
        >
          Add News
        </button>
      </div>

      {isAddingNew && (
        <NewsForm
          item={newItem}
          onSubmit={handleSubmitNew}
          submitLabel="Create News"
          onNewsUpdate={handleUpdateNewItem}
          onCancel={() => setIsAddingNew(false)}
        />
      )}

      <div className="space-y-4">
        {news && news.length > 0 ? (
          news.map((item) => (
            <div
              key={item.id}
              className="bg-white shadow rounded-lg p-4"
            >
              {editingNews?.id === item.id ? (
                <NewsForm
                  item={editingNews || {}}
                  onSubmit={handleSubmitEdit}
                  submitLabel="Update News"
                  onNewsUpdate={handleUpdateEditingNews}
                  onCancel={() => setEditingNews(null)}
                />
              ) : (
                <div>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <h3 className="text-lg font-medium text-gray-900">{item.title}</h3>
                        {item.featured && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            Featured
                          </span>
                        )}
                        {item.status && (
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            item.status === 'published' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                          </span>
                        )}
                      </div>
                      <p className="mt-1 text-sm text-gray-500">
                        {item.author && `By ${item.author}`} {item.publishDate && `| ${new Date(item.publishDate).toLocaleDateString()}`}
                      </p>
                      <div className="mt-2 prose prose-sm max-w-none text-gray-600">
                        {item.content}
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setEditingNews(item)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => onDeleteNews(item.id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                  {item.category && (
                    <div className="mt-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        {item.category.charAt(0).toUpperCase() + item.category.slice(1)}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="bg-white shadow rounded-lg p-6 text-center">
            <p className="text-gray-500">No news items available. Click "Add News" to create your first news item.</p>
            <button
              onClick={() => setIsAddingNew(true)}
              className="mt-4 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
            >
              Add News
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default NewsManagement;
